import { parseAsString, parseAsStringEnum, useQueryState } from 'nuqs'
import { useCallback } from 'react'
import { useGetScan } from '../../utils/api-client/api-hooks.ts'
import { isScaTool } from '../../utils/detector-utils.ts'
import { OutcomeReferenceTabSchema, type OutcomeReferenceTab } from '../outcomes/outcome-types.ts'

/**
 * Query params that open a finding drawer on the scan details page. Exported so
 * that anything linking *into* a drawer builds the same URL this hook reads —
 * see `Routes.ScanDetailsPage.createPath`.
 */
export const TRIAGE_FINDING_ID_PARAM = 'triage-finding-id'
export const FIX_FINDING_ID_PARAM = 'fix-finding-id'

export function useNavigateScanFindingDrawers({ scanId }: { scanId: string }) {
  const { data: scan } = useGetScan({ id: scanId })

  const [findingTriageFindingId, setFindingTriageFindingId] = useQueryState(
    TRIAGE_FINDING_ID_PARAM,
    parseAsString.withOptions({ history: 'push' })
  )

  const [findingFixFindingId, setFindingFixFindingId] = useQueryState(
    FIX_FINDING_ID_PARAM,
    parseAsString.withOptions({ history: 'push' })
  )

  const [resultId, setResultId] = useQueryState('result-id', parseAsString.withOptions({ history: 'push' }))

  const [outcomesReferenceTab, setOutcomesReferenceTab] = useQueryState(
    'outcomes-reference-tab',
    parseAsStringEnum<OutcomeReferenceTab>(OutcomeReferenceTabSchema.options).withOptions({ history: 'push' })
  )

  const isSca = scan ? isScaTool(scan.detector) : false

  const onClose = useCallback(() => {
    setFindingTriageFindingId(null)
    setFindingFixFindingId(null)
    setResultId(null)
    setOutcomesReferenceTab(null)
  }, [setFindingTriageFindingId, setFindingFixFindingId, setResultId, setOutcomesReferenceTab])

  const onOpenFindingTriageDrawer = useCallback(
    (findingId: string | undefined, resultIdParam?: string) => {
      setFindingTriageFindingId(findingId || null)
      setFindingFixFindingId(null)
      setResultId(resultIdParam ?? null)
      setOutcomesReferenceTab(null)
    },
    [setFindingTriageFindingId, setFindingFixFindingId, setResultId, setOutcomesReferenceTab]
  )

  const onOpenFindingFixDrawer = useCallback(
    (findingId: string | undefined, resultIdParam?: string) => {
      setFindingFixFindingId(findingId || null)
      setFindingTriageFindingId(null)
      setResultId(resultIdParam ?? null)
      setOutcomesReferenceTab(null)
    },
    [setFindingFixFindingId, setFindingTriageFindingId, setResultId, setOutcomesReferenceTab]
  )

  const onNavigateToResult = useCallback((newResultId: string) => setResultId(newResultId), [setResultId])

  const onOpenOutcomesReferenceDrawer = useCallback(
    (tab: OutcomeReferenceTab) => {
      setOutcomesReferenceTab(tab)
      setFindingTriageFindingId(null)
      setFindingFixFindingId(null)
      setResultId(null)
    },
    [setOutcomesReferenceTab, setFindingTriageFindingId, setFindingFixFindingId, setResultId]
  )

  const onCloseOutcomesReferenceDrawer = useCallback(() => setOutcomesReferenceTab(null), [setOutcomesReferenceTab])

  const onOutcomesReferenceTabChange = useCallback(
    (tab: OutcomeReferenceTab) => setOutcomesReferenceTab(tab),
    [setOutcomesReferenceTab]
  )

  return {
    scanId,
    onClose,
    onOpenFindingTriageDrawer,
    onOpenFindingFixDrawer,
    onNavigateToResult,
    onOpenOutcomesReferenceDrawer,
    onCloseOutcomesReferenceDrawer,
    onOutcomesReferenceTabChange,
    findingTriageFindingId: findingTriageFindingId || undefined,
    findingFixFindingId: findingFixFindingId || undefined,
    resultId: resultId || undefined,
    outcomesReferenceTab: outcomesReferenceTab ?? undefined,
    isSca,
  }
}
